/**
 * GestureCreatorOverlay.tsx
 * 「シミュレーター」画面。ChatAppOverlay と同じくフルスクリーンのオーバーレイとして表示する。
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, CircleHelp, CloudDownload, LoaderCircle, Lock, Play, Plus, Save, Square, Trash2, X } from 'lucide-react';
import { useSetAtom } from 'jotai';
import { ProductTour, type ProductTourStep } from '@/components/ProductTour';
import {
  addCustomMotion,
  type CustomMotionPose,
  customMotionToTimeline,
  getCustomMotion,
  removeCustomMotion,
  useCustomMotions,
} from '@/flow/config/customMotions';
import { VirtualRobot } from '@/flow/components/VirtualRobot';
import type { BodyRotationAxis, SelectedWegoJoint, WegoJointControl } from '@/flow/animation/useWegoAnimationScene';
import { setFlowMotionAtom } from '@/flow/atoms/flowMotionAtom';
import { nextFlowRequestId } from '@/flow/atoms/flowRequestId';
import { useFiloRemote } from '@/connection/FiloRemoteContext';
import { RpcRemoteError } from '@/connection/rpcTypes';
import { SOUND_EFFECTS, type SoundEffect } from '@/flow/config/soundEffects';
import { unlockSpeechAudioContext } from '@/speech/speechAudioContext';
import { JOINT_VID_MAP, WEGO_JOINTS } from '@/flow/animation/wegoJoints';
import { GESTURE_MOTIONS } from '@/flow/config/gestureMotions';
import {
  DEFAULT_FPS,
  MOTION_LIBRARY_URL,
  parseMotionLibrary,
  type MoveTrackId,
  type WegoMotion,
} from '@/flow/animation/animationUtils';
import { confirmCreateTour, hasConfirmedCreateTour } from '@/flow/config/gestureCreator';

export type GestureCreatorOverlayProps = {
  onClose: () => void;
  userSub: string;
  initialMotionId?: string | null;
  startTour?: boolean;
};

export const getRobotAngleReadButtonState = (
  representativeRobotId: string | null,
  loading: boolean,
) => ({
  disabled: !representativeRobotId || loading,
  ariaLabel: loading ? '関節情報を取得中' : '接続中のロボットから関節情報を取得',
  title: loading
    ? 'Loading...'
    : representativeRobotId ? '接続中のロボットから関節情報を取得' : 'ロボット未接続',
});

/** タイムライン上の1コマ。並び順がそのまま再生順になる。 */
export type GestureFrame = {
  key: string;
  joints: Record<string, number>;
  movement?: Partial<Record<MoveTrackId, number>>;
  durationMs?: number;
  ledColor?: string;
  sound?: SoundEffect;
};

export const shouldShowEmptyFrameDelete = (
  previewPlaying: boolean,
  selectedFrameIndex: number,
  selectedFrame: GestureFrame | undefined,
): boolean => (
  !previewPlaying
  && selectedFrameIndex > 0
  && Object.keys(selectedFrame?.joints ?? {}).length === 0
);

const PLAYBACK_SPEEDS = [1, 2, 3, 4, 5];
const BODY_ROTATION_AXES: { axis: BodyRotationAxis; label: string }[] = [
  { axis: 'x', label: 'X軸' },
  { axis: 'y', label: 'Y軸' },
  { axis: 'z', label: 'Z軸' },
];
const GESTURE_TOUR_STEPS: ProductTourStep[] = [
  {
    target: '[data-product-tour="gesture-robot"]',
    title: 'ロボットの姿勢をつくる',
    description: '3Dロボットの関節をドラッグするか、右側の「関節の調整」で角度を変えます。',
  },
  {
    target: '[data-product-tour="gesture-joints"]',
    title: '角度を細かく調整する',
    description: '関節名を開き、スライダーや数値入力で姿勢を整えます。接続中のロボットから姿勢を取得することもできます。',
  },
  {
    target: '[data-product-tour="gesture-frame"]',
    title: '姿勢をフレームに登録する',
    description: '空のフレームにある＋を押すと、現在の姿勢が登録されます。複数の姿勢を登録すると順番に再生されます。',
  },
  {
    target: '[data-product-tour="gesture-save"]',
    title: 'ジェスチャーを保存する',
    description: '保存を押して名前を入力します。保存後はCreateカードでこのジェスチャーを選べます。',
  },
  {
    target: '[data-product-tour="gesture-load"]',
    title: '保存したジェスチャーを読み込む',
    description: 'よみこみから保存済みのジェスチャーを選ぶと、編集や再生ができます。',
  },
  {
    target: '[data-product-tour="gesture-delete"]',
    title: 'ジェスチャーを削除する',
    description: '削除したいジェスチャーを読み込み、削除を押して確認すると一覧から削除されます。',
  },
];
const GREETING_TOUR_STEPS: ProductTourStep[] = [
  {
    target: '[data-product-tour="gesture-timeline"]',
    title: 'あいさつの動きを確認する',
    description: '「右手バイバイ」と同じ姿勢を再生順に用意しました。右肩と足首を連動させて、右手を3回振ります。',
  },
  {
    target: '[data-product-tour="greeting-hold-frame"]',
    title: '3番目の姿勢を長く固定する',
    description: '同じ3番目の姿勢を空欄の前後に登録すると、その間は同じ姿勢のまま固定されます。空欄を増やすほど固定時間が長くなります。',
  },
  {
    target: '[data-product-tour="gesture-play"]',
    title: 'あいさつを再生する',
    description: '再生を押して、ロボットが右腕を振る動きを確認してから「次へ」を押します。',
  },
  {
    target: '[data-product-tour="gesture-save"]',
    title: 'あいさつを保存する',
    description: '保存を押して保存画面を開いてから「次へ」を押します。',
  },
  {
    target: '[data-product-tour="gesture-save-confirm"]',
    title: '名前を付けて保存する',
    description: '名前には「あいさつ」が入力されています。保存を押し、画面が閉じてから「次へ」を押します。',
  },
  {
    target: '[data-product-tour="gesture-delete"]',
    title: '作った動きを削除する',
    description: '削除を押して確認画面を開いてから「次へ」を押します。',
  },
  {
    target: '[data-product-tour="gesture-delete-confirm"]',
    title: '削除を完了する',
    description: '確認画面の削除を押すと、練習で作った「あいさつ」が一覧から削除されます。',
  },
];
/** 再生速度 x1 のときのフレーム間隔。 */
const BASE_FRAME_INTERVAL_MS = 1000;
const frameDurationMs = (_frame: GestureFrame, speed: number) => (
  BASE_FRAME_INTERVAL_MS / Math.max(1, speed)
);

const DEFAULT_FRAME_JOINTS: Record<string, number> = Object.fromEntries(
  Object.entries(WEGO_JOINTS)
    .filter(([, joint]) => joint.isMotor)
    .map(([jointId, joint]) => [jointId, joint.default]),
);

let frameKeySeed = 0;
const createFrameKey = () => {
  frameKeySeed += 1;
  return `frame-${frameKeySeed}`;
};

const createDefaultFrame = (): GestureFrame => ({
  key: createFrameKey(),
  joints: { ...DEFAULT_FRAME_JOINTS },
});

const greetingPose = (rightShoulder: number, rightAnkleRoll: number, leftAnkleRoll: number) => ({
  RIGHT_SHOULDER: rightShoulder,
  LEFT_SHOULDER: 20,
  RIGHT_HIP: -9,
  RIGHT_ANKLE_PITCH: -8,
  RIGHT_ANKLE_ROLL: rightAnkleRoll,
  LEFT_HIP: 9,
  LEFT_ANKLE_PITCH: 8,
  LEFT_ANKLE_ROLL: leftAnkleRoll,
});

const createGreetingFrame = (rightShoulder: number, rightAnkleRoll: number, leftAnkleRoll: number): GestureFrame => ({
  key: createFrameKey(),
  joints: greetingPose(rightShoulder, rightAnkleRoll, leftAnkleRoll),
});

const createGreetingFrames = (): GestureFrame[] => {
  const thirdPose = greetingPose(-20, 0, 0);
  return [
    createGreetingFrame(-20, 1, -1),
    createEmptyFrame(),
    createGreetingFrame(127, 0, 0),
    createEmptyFrame(),
    createGreetingFrame(127, 0, 0),
    createEmptyFrame(),
    { key: createFrameKey(), joints: { ...thirdPose } },
    
  ];
};

const createEmptyFrame = (): GestureFrame => ({
  key: createFrameKey(),
  joints: {},
});

export const insertEmptyFrameAfter = (
  frames: GestureFrame[],
  selectedIndex: number,
): { frames: GestureFrame[]; insertedIndex: number } => {
  const insertionIndex = Math.min(frames.length, Math.max(0, selectedIndex + 1));
  const nextFrames = [...frames];
  nextFrames.splice(insertionIndex, 0, createEmptyFrame());
  return { frames: nextFrames, insertedIndex: insertionIndex };
};

export const framesToPoses = (frames: GestureFrame[], speed: number): CustomMotionPose[] => {
  let elapsedMs = 0;
  return frames.map((frame) => {
    elapsedMs += frameDurationMs(frame, speed);
    const pose = {
      timeMs: Math.round(elapsedMs),
      joints: frame.joints,
      movement: movementWithBodyRotation(
        frame.movement,
        bodyRotationFromMovement(frame.movement),
      ),
      ledColor: frame.ledColor,
      sound: frame.sound,
    };
    return pose;
  });
};

export const getFrameIndexAtElapsed = (
  frames: GestureFrame[],
  speed: number,
  elapsedMs: number,
): number => {
  if (frames.length === 0) return 0;
  let frameEndMs = 0;
  let reachedFrameIndex = 0;
  const currentTimelineFrame = Math.round((Math.max(0, elapsedMs) / 1000) * DEFAULT_FPS);
  for (let index = 0; index < frames.length; index += 1) {
    frameEndMs += frameDurationMs(frames[index], speed);
    const targetTimelineFrame = Math.round((frameEndMs / 1000) * DEFAULT_FPS);
    if (currentTimelineFrame < targetTimelineFrame) return reachedFrameIndex;
    reachedFrameIndex = index;
  }
  return frames.length - 1;
};

export const bodyRotationFromMovement = (
  movement?: Partial<Record<MoveTrackId, number>>,
): Record<BodyRotationAxis, number> => ({
  x: Number(movement?.bodyRotationX) || 0,
  y: Number(movement?.bodyRotationY) || 0,
  z: Number(movement?.bodyRotationZ) || 0,
});

export const movementWithBodyRotation = (
  movement: Partial<Record<MoveTrackId, number>> | undefined,
  rotation: Record<BodyRotationAxis, number>,
): Partial<Record<MoveTrackId, number>> => ({
  ...movement,
  bodyRotationX: rotation.x,
  bodyRotationY: rotation.y,
  bodyRotationZ: rotation.z,
});

export const registerFramePose = (
  frame: GestureFrame,
  joints: Record<string, number>,
  rotation: Record<BodyRotationAxis, number>,
  ledColor?: string,
  sound?: SoundEffect,
): GestureFrame => ({
  ...frame,
  joints,
  movement: movementWithBodyRotation(frame.movement, rotation),
  ledColor,
  sound,
});

export const clearFramePose = (frame: GestureFrame): GestureFrame => ({
  ...frame,
  joints: {},
  movement: undefined,
  ledColor: undefined,
  sound: undefined,
});

export const updateFrameSound = (
  frames: GestureFrame[],
  selectedFrameIndex: number,
  sound: SoundEffect | null,
): GestureFrame[] => frames.map((frame, frameIndex) => (
  frameIndex === selectedFrameIndex
    ? { ...frame, sound: sound ?? undefined }
    : frame
));

export const motionToGestureFrames = (motion: WegoMotion): GestureFrame[] => {
  return motion.frames.map((motionFrame) => {
    const frame: GestureFrame = {
      key: createFrameKey(),
      joints: { ...motionFrame.jointsDegrees },
      movement: motionFrame.movement ? { ...motionFrame.movement } : undefined,
      durationMs: Number.isFinite(motionFrame.durationMs) ? motionFrame.durationMs : 200,
    };
    return frame;
  });
};

const createRandomMotionId = (existingIds: Set<string>) => {
  let id = '';
  do {
    id = String(Math.floor(1000 + Math.random() * 9000));
  } while (existingIds.has(id));
  return id;
};

type JointNumberInputProps = {
  joint: WegoJointControl;
  onValueChange: (value: number) => void;
  onFocus: () => void;
  onBlur: () => void;
};

const formatJointValue = (joint: WegoJointControl) => String(Math.round(joint.value));

function JointNumberInput({ joint, onValueChange, onFocus, onBlur }: JointNumberInputProps) {
  const [draft, setDraft] = useState(() => formatJointValue(joint));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(formatJointValue(joint));
  }, [joint]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDraft(event.currentTarget.value);
  };

  const commit = (input: HTMLInputElement) => {
    if (!Number.isFinite(input.valueAsNumber)) return;
    onValueChange(Math.round(input.valueAsNumber));
  };

  const handleFocus = () => {
    focusedRef.current = true;
    onFocus();
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    commit(event.currentTarget);
    focusedRef.current = false;
    setDraft(Number.isFinite(event.currentTarget.valueAsNumber)
      ? String(Math.round(event.currentTarget.valueAsNumber))
      : formatJointValue(joint));
    onBlur();
  };

  const handleKeyUp = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') commit(event.currentTarget);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.currentTarget.blur();
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLInputElement>) => {
    commit(event.currentTarget);
  };

  return (
    <input
      type="number"
      min={joint.min}
      max={joint.max}
      step={1}
      value={draft}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onPointerUp={handlePointerUp}
    />
  );
}

export function GestureCreatorOverlay({
  onClose,
  userSub,
  initialMotionId = null,
  startTour = false,
}: GestureCreatorOverlayProps) {
  const customMotions = useCustomMotions();
  const setFlowMotion = useSetAtom(setFlowMotionAtom);
  const [label, setLabel] = useState('');
  const [motionId, setMotionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jointControls, setJointControls] = useState<WegoJointControl[]>([]);
  const [setJointValue, setSetJointValue] = useState<((jointId: string, value: number) => void) | null>(null);
  const [setJointPoseValue, setSetJointPoseValue] = useState<((jointId: string, value: number) => void) | null>(null);
  const [setBodyRotationValue, setSetBodyRotationValue] = useState<((axis: BodyRotationAxis, degrees: number) => void) | null>(null);
  const [bodyRotation, setBodyRotation] = useState<Record<BodyRotationAxis, number>>({ x: 0, y: 0, z: 0 });
  const bodyRotationRef = useRef<Record<BodyRotationAxis, number>>({ x: 0, y: 0, z: 0 });
  const [selectedJoint, setSelectedJoint] = useState<SelectedWegoJoint | null>(null);
  const [adjustingJointId, setAdjustingJointId] = useState<string | null>(null);
  const [initialFrames] = useState<GestureFrame[]>(() => [
    createDefaultFrame(),
    createEmptyFrame(),
    createEmptyFrame(),
    createEmptyFrame(),
    createEmptyFrame(),
  ]);
  const [frames, setFrames] = useState<GestureFrame[]>(initialFrames);
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [snapshots, setSnapshots] = useState<Record<string, string>>({});
  const [pendingSnapshotFrames, setPendingSnapshotFrames] = useState<GestureFrame[] | null>(initialFrames);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [snapshotReady, setSnapshotReady] = useState(false);
  const [dragFrameIndex, setDragFrameIndex] = useState<number | null>(null);
  const [dropFrameIndex, setDropFrameIndex] = useState<number | null>(null);
  const [ledColor] = useState<string | null>(null);
  const [sound, setSound] = useState<SoundEffect | null>(null);
  const [jointControlsOpen, setJointControlsOpen] = useState(false);
  const [robotAnglesLoading, setRobotAnglesLoading] = useState(false);
  const [soundControlsOpen, setSoundControlsOpen] = useState(false);
  const [showCollisionBoxes, setShowCollisionBoxes] = useState(false);
  const remote = useFiloRemote();
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogLabel, setSaveDialogLabel] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [loadDialogMotionId, setLoadDialogMotionId] = useState('');
  const [gestureLibrary, setGestureLibrary] = useState<WegoMotion[]>([]);
  const [gestureLibraryStatus, setGestureLibraryStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [tourMode, setTourMode] = useState<'guide' | 'greeting' | null>(startTour ? 'guide' : null);
  const [tourPromptOpen, setTourPromptOpen] = useState(
    () => !startTour && !hasConfirmedCreateTour(userSub),
  );
  const previewStoppedRef = useRef(true);
  const jointCardRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const captureSnapshotRef = useRef<(() => string | null) | null>(null);
  const framesRef = useRef(frames);
  const playbackSpeedRef = useRef(playbackSpeed);
  framesRef.current = frames;
  playbackSpeedRef.current = playbackSpeed;
  const representativeRobotId = remote.snapshot.representativeRobotId;
  const robotAngleReadButtonState = getRobotAngleReadButtonState(
    representativeRobotId,
    robotAnglesLoading,
  );

  const resolveTourPrompt = (showTour: boolean) => {
    confirmCreateTour(userSub);
    setTourPromptOpen(false);
    if (showTour) setTourMode('guide');
  };

  useEffect(() => {
    if (!selectedJoint) return;
    jointCardRefs.current[selectedJoint.id]?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [selectedJoint]);

  const handleJointControlReady = useCallback((setValue: (jointId: string, value: number) => void) => {
    setSetJointValue(() => setValue);
  }, []);

  const handleJointPoseReady = useCallback((setValue: (jointId: string, value: number) => void) => {
    setSetJointPoseValue(() => setValue);
  }, []);

  const handleBodyRotationReady = useCallback((setValue: (axis: BodyRotationAxis, degrees: number) => void) => {
    setSetBodyRotationValue(() => setValue);
  }, []);

  const updateBodyRotation = (axis: BodyRotationAxis, degrees: number) => {
    if (!Number.isFinite(degrees)) return;
    const clamped = Math.min(180, Math.max(-180, degrees));
    const nextRotation = { ...bodyRotationRef.current, [axis]: clamped };
    bodyRotationRef.current = nextRotation;
    setBodyRotation(nextRotation);
    setBodyRotationValue?.(axis, clamped);
  };

  const handleBodyRotationChange = useCallback((rotation: Record<BodyRotationAxis, number>) => {
    bodyRotationRef.current = rotation;
    setBodyRotation(rotation);
  }, []);

  const handleSnapshotReady = useCallback((captureSnapshot: () => string | null) => {
    captureSnapshotRef.current = captureSnapshot;
    setSnapshotReady(true);
  }, []);

  const handleMotionElapsedChange = useCallback((elapsedMs: number) => {
    setSelectedFrameIndex(getFrameIndexAtElapsed(
      framesRef.current,
      playbackSpeedRef.current,
      elapsedMs,
    ));
  }, []);

  const updateJointValue = (jointId: string, value: number) => {
    if (!Number.isFinite(value)) return;
    setAdjustingJointId(jointId);
    setJointValue?.(jointId, value);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (document.querySelector('.product-tour')) return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const saveMotion = (motionLabel: string) => {
    const trimmedLabel = motionLabel.trim();
    if (!trimmedLabel) {
      setError('ジェスチャーのなまえを入力してください');
      return;
    }
    const id = motionId ?? createRandomMotionId(new Set(customMotions.map((motion) => motion.id)));
    addCustomMotion({
      id,
      label: trimmedLabel,
      poses: framesToPoses(frames, playbackSpeed),
    });
    setMotionId(id);
    setLabel(trimmedLabel);
    setError(null);
  };

  const openSaveDialog = () => {
    setSaveDialogLabel(label);
    setSaveDialogOpen(true);
  };

  const readRobotAngles = async () => {
    if (!representativeRobotId || robotAnglesLoading) return;
    setRobotAnglesLoading(true);
    setError(null);
    try {
      const requestAngles = () => remote.callRobot<Record<string, number | null>>(
          representativeRobotId,
          'getCurrentAngles',
          Object.values(JOINT_VID_MAP),
          { timeoutMs: 8000 },
        );
      let angles: Record<string, number | null>;
      try {
        angles = await requestAngles();
      } catch (readError) {
        if (!(readError instanceof RpcRemoteError) || !readError.payload.retryable) throw readError;
        await new Promise((resolve) => { window.setTimeout(resolve, 250); });
        angles = await requestAngles();
      }
      Object.entries(JOINT_VID_MAP).forEach(([jointId, vid]) => {
        const rawAngle = angles[String(vid)];
        if (typeof rawAngle === 'number') setJointPoseValue?.(jointId, rawAngle / 10);
      });
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : '関節情報を取得できませんでした');
    } finally {
      setRobotAnglesLoading(false);
    }
  };

  const confirmSaveMotion = () => {
    saveMotion(saveDialogLabel);
    if (saveDialogLabel.trim()) setSaveDialogOpen(false);
  };

  const resetFrames = () => {
    const nextFrames = [
      createDefaultFrame(),
      createEmptyFrame(),
      createEmptyFrame(),
      createEmptyFrame(),
      createEmptyFrame(),
    ];
    setFrames(nextFrames);
    setSnapshots({});
    setPendingSnapshotFrames(nextFrames);
    setSelectedFrameIndex(0);
  };

  const startGreetingTour = () => {
    const greetingFrames = createGreetingFrames();
    setMotionId(null);
    setLabel('あいさつ');
    setFrames(greetingFrames);
    setSnapshots({});
    setPendingSnapshotFrames(greetingFrames);
    setSelectedFrameIndex(0);
    setPlaybackSpeed(2);
    setSound(null);
    setError(null);
    Object.entries(greetingFrames[0].joints).forEach(([jointId, value]) => setJointPoseValue?.(jointId, value));
    setTourMode('greeting');
  };

  const loadMotion = (id: string) => {
    if (!id) {
      setMotionId(null);
      setLabel('');
      resetFrames();
      setPlaybackSpeed(1);
      setSound(null);
      setError(null);
      return;
    }
    const motion = getCustomMotion(id);
    if (!motion) return;
    const loadedFrames: GestureFrame[] = motion.poses.map((pose) => ({
      key: createFrameKey(),
      joints: pose.joints,
      movement: pose.movement ? { ...pose.movement } : undefined,
      ledColor: pose.ledColor,
      sound: pose.sound,
    }));
    const nextFrames = loadedFrames.length > 0 ? loadedFrames : [createDefaultFrame()];
    // 保存時のフレーム間隔から再生速度を復元する。
    const intervalMs = motion.poses.length > 1 ? motion.poses[1].timeMs - motion.poses[0].timeMs : BASE_FRAME_INTERVAL_MS;
    setPlaybackSpeed(Math.min(5, Math.max(1, Math.round(BASE_FRAME_INTERVAL_MS / Math.max(1, intervalMs)))));
    setMotionId(motion.id);
    setLabel(motion.label);
    setFrames(nextFrames);
    setSnapshots({});
    setPendingSnapshotFrames(nextFrames);
    setSelectedFrameIndex(0);
    setSound(nextFrames[0].sound ?? null);
    setError(null);
  };

  const openLoadDialog = () => {
    setLoadDialogMotionId(motionId ?? '');
    setLoadDialogOpen(true);
  };

  useEffect(() => {
    if (!loadDialogOpen) return;
    const controller = new AbortController();
    setGestureLibraryStatus('loading');
    void fetch(MOTION_LIBRARY_URL, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<unknown>;
      })
      .then((value) => {
        setGestureLibrary(parseMotionLibrary(value));
        setGestureLibraryStatus('ready');
      })
      .catch(() => {
        if (!controller.signal.aborted) setGestureLibraryStatus('error');
      });
    return () => controller.abort();
  }, [loadDialogOpen]);

  const importGestureMotion = (motionId: string) => {
    const sourceMotion = gestureLibrary.find((motion) => String(motion.motionNo) === motionId);
    if (!sourceMotion) return;
    const importedFrames = motionToGestureFrames(sourceMotion);
    if (importedFrames.length === 0) return;
    const gestureLabel = GESTURE_MOTIONS.find((motion) => motion.id === motionId)?.label ?? sourceMotion.name;
    setMotionId(null);
    setLabel(gestureLabel);
    setFrames(importedFrames);
    setSnapshots({});
    setPendingSnapshotFrames(importedFrames);
    setSelectedFrameIndex(0);
    setPlaybackSpeed(1);
    setSound(importedFrames[0].sound ?? null);
    setError(null);
    setLoadDialogOpen(false);
  };

  const confirmLoadMotion = () => {
    if (loadDialogMotionId) loadMotion(loadDialogMotionId);
    setLoadDialogOpen(false);
  };

  const deleteMotion = () => {
    if (!motionId) return;
    removeCustomMotion(motionId);
    setMotionId(null);
    setLabel('');
    resetFrames();
    setSound(null);
    setDeleteDialogOpen(false);
  };

  useEffect(() => {
    if (initialMotionId) loadMotion(initialMotionId);
  }, [initialMotionId]);

  // 読み込んだ姿勢は 3D シーンの準備完了後にひとつずつ再現してサムネイルを撮影する。
  useEffect(() => {
    if (!pendingSnapshotFrames || !snapshotReady || !setJointPoseValue || !setBodyRotationValue) return;
    let cancelled = false;
    setSnapshotBusy(true);
    const nextAnimationFrame = () => new Promise<void>((resolve) => { requestAnimationFrame(() => resolve()); });
    void (async () => {
      const captured: Record<string, string> = {};
      for (const frame of pendingSnapshotFrames) {
        if (Object.keys(frame.joints).length === 0) continue;
        Object.entries(frame.joints).forEach(([jointId, value]) => setJointPoseValue(jointId, value));
        const frameBodyRotation = bodyRotationFromMovement(frame.movement);
        BODY_ROTATION_AXES.forEach(({ axis }) => setBodyRotationValue(axis, frameBodyRotation[axis]));
        await nextAnimationFrame();
        if (cancelled) return;
        const image = captureSnapshotRef.current?.();
        if (image) captured[frame.key] = image;
      }
      const firstFrame = pendingSnapshotFrames[0];
      if (firstFrame) {
        Object.entries(firstFrame.joints).forEach(([jointId, value]) => setJointPoseValue(jointId, value));
        const firstBodyRotation = bodyRotationFromMovement(firstFrame.movement);
        BODY_ROTATION_AXES.forEach(({ axis }) => setBodyRotationValue(axis, firstBodyRotation[axis]));
        bodyRotationRef.current = firstBodyRotation;
        setBodyRotation(firstBodyRotation);
      }
      setSnapshots(captured);
      setPendingSnapshotFrames(null);
      setSnapshotBusy(false);
    })();
    return () => { cancelled = true; };
  }, [pendingSnapshotFrames, setBodyRotationValue, setJointPoseValue, snapshotReady]);

  const currentPoseJoints = () => Object.fromEntries(jointControls.map((joint) => [joint.id, joint.value]));

  const insertFrame = () => {
    const { frames: nextFrames, insertedIndex } = insertEmptyFrameAfter(frames, selectedFrameIndex);
    setFrames(nextFrames);
    setSelectedFrameIndex(insertedIndex);
  };

  const updateFrame = (index: number) => {
    const target = frames[index];
    if (index === 0 || !target || jointControls.length === 0) return;
    const joints = currentPoseJoints();
    setFrames((current) => current.map((frame, frameIndex) => (frameIndex === index
      ? registerFramePose(
        frame,
        joints,
        bodyRotationRef.current,
        ledColor ?? undefined,
        sound ?? undefined,
      )
      : frame)));
    const image = captureSnapshotRef.current?.();
    if (image) setSnapshots((current) => ({ ...current, [target.key]: image }));
  };

  const updateSound = (nextSound: SoundEffect | null) => {
    setSound(nextSound);
    setFrames((current) => updateFrameSound(current, selectedFrameIndex, nextSound));
  };

  const clearFrame = (index: number) => {
    const target = frames[index];
    if (index === 0 || !target) return;
    setFrames((current) => current.map((frame, frameIndex) => (
      frameIndex === index ? clearFramePose(frame) : frame
    )));
    setSnapshots((current) => {
      const next = { ...current };
      delete next[target.key];
      return next;
    });
  };

  const removeFrame = (index: number) => {
    const target = frames[index];
    if (index === 0 || !target) return;
    setFrames((current) => current.filter((_, frameIndex) => frameIndex !== index));
    setSnapshots((current) => {
      const next = { ...current };
      delete next[target.key];
      return next;
    });
    setSelectedFrameIndex((current) => Math.max(0, Math.min(current, frames.length - 2)));
  };

  const moveFrame = (from: number, to: number) => {
    if (from === to || from <= 0 || to <= 0 || from >= frames.length || to >= frames.length) return;
    setFrames((current) => {
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setSelectedFrameIndex(to);
  };

  // タッチ端末では HTML5 Drag and Drop が発火しないため、Pointer Events で並び替え・タップ選択を実装する。
  const framePointerDragRef = useRef<{ index: number; locked: boolean; pointerId: number; startX: number; startY: number; moved: boolean } | null>(null);

  const handleFrameDragPointerDown = (index: number, locked: boolean) => (event: React.PointerEvent<HTMLDivElement>) => {
    if (snapshotBusy) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    framePointerDragRef.current = {
      index,
      locked,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    if (event.pointerType === 'mouse') event.preventDefault();
  };

  const handleFrameDragPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = framePointerDragRef.current;
    if (!state || state.pointerId !== event.pointerId || state.locked) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    if (!state.moved && Math.hypot(dx, dy) < 8) return;
    if (!state.moved) {
      state.moved = true;
      setDragFrameIndex(state.index);
    }
    event.preventDefault();
    const target = document.elementFromPoint(event.clientX, event.clientY);
    const cardEl = target instanceof Element ? target.closest<HTMLElement>('[data-frame-index]') : null;
    const dropIndex = cardEl ? Number(cardEl.dataset.frameIndex) : NaN;
    setDropFrameIndex(Number.isInteger(dropIndex) && dropIndex > 0 ? dropIndex : null);
  };

  const releaseFrameDragCapture = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleFrameDragPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = framePointerDragRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    framePointerDragRef.current = null;
    releaseFrameDragCapture(event);
    if (state.moved) {
      if (dropFrameIndex !== null) moveFrame(state.index, dropFrameIndex);
      setDragFrameIndex(null);
      setDropFrameIndex(null);
    } else {
      selectFrame(state.index);
    }
  };

  const handleFrameDragPointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = framePointerDragRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    framePointerDragRef.current = null;
    releaseFrameDragCapture(event);
    setDragFrameIndex(null);
    setDropFrameIndex(null);
  };


  const selectFrame = (index: number) => {
    const frame = frames[index];
    if (!frame) return;
    setSelectedFrameIndex(index);
    Object.entries(frame.joints).forEach(([jointId, value]) => setJointPoseValue?.(jointId, value));
    const frameBodyRotation = bodyRotationFromMovement(frame.movement);
    BODY_ROTATION_AXES.forEach(({ axis }) => setBodyRotationValue?.(axis, frameBodyRotation[axis]));
    bodyRotationRef.current = frameBodyRotation;
    setBodyRotation(frameBodyRotation);
    setSound(typeof frame.sound === 'string' ? frame.sound : null);
  };

  const stopPreview = useCallback(() => {
    previewStoppedRef.current = true;
    setFlowMotion(null);
    setPreviewPlaying(false);
    setSelectedFrameIndex(0);
  }, [setFlowMotion]);

  const startPreview = useCallback(() => {
    unlockSpeechAudioContext();
    const timeline = customMotionToTimeline({
      id: motionId ?? 'preview',
      label: label.trim() || 'プレビュー',
      poses: framesToPoses(frames, playbackSpeed),
    });
    // setFlowMotion は置換する前の完了コールバックを同期的に実行するため、再生要求の入替中は繰り返さない。
    previewStoppedRef.current = true;
    setSelectedFrameIndex(0);
    setPreviewPlaying(true);
    setFlowMotion({
      motionNo: 0,
      requestId: nextFlowRequestId(),
      timeline,
      onComplete: () => {
        setPreviewPlaying(false);
        setSelectedFrameIndex(Math.max(0, frames.length - 1));
      },
    });
    previewStoppedRef.current = false;
  }, [frames, label, motionId, playbackSpeed, setFlowMotion]);

  useEffect(() => () => {
    previewStoppedRef.current = true;
    setFlowMotion(null);
  }, [setFlowMotion]);

  return createPortal(
    <div
      className="gesture-creator-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gesture-creator-title"
    >
      <header className="gesture-creator-overlay__header">
        <div className="gesture-creator-overlay__header-content">
          <div>
            <p className="eyebrow">FILO APP</p>
            <h2 id="gesture-creator-title">シミュレーター</h2>
            <p>じぶんだけのうごきをつくる</p>
          </div>

          <div className="gesture-creator-overlay__toolbar">
            {motionId && label.trim() && (
              <strong className="gesture-creator-overlay__motion-label" title={label}>{label}</strong>
            )}
            <button
              type="button"
              className="gesture-creator-tool-button"
              onClick={() => setTourMode('guide')}
              aria-label="ジェスチャー作成の使い方"
              title="使い方"
            >
              <CircleHelp aria-hidden="true" size={16} />
            </button>
            <button type="button" className="gesture-creator-tool-button" onClick={openLoadDialog} disabled={snapshotBusy} data-product-tour="gesture-load">
              よみこみ
            </button>
            <button type="button" className="gesture-creator-submit" onClick={openSaveDialog} disabled={snapshotBusy} data-product-tour="gesture-save">
              <Save aria-hidden="true" size={16} />
              保存
            </button>
            <label className="gesture-creator-speed">
              <span className="sr-only">再生速度</span>
              <select
                value={playbackSpeed}
                onChange={(event) => setPlaybackSpeed(Number(event.target.value))}
                disabled={snapshotBusy}
                title="再生速度（x1 は1秒間隔）"
              >
                {PLAYBACK_SPEEDS.map((speed) => <option key={speed} value={speed}>x{speed}</option>)}
              </select>
            </label>
            <button type="button" className="gesture-creator-tool-button" onClick={startPreview} disabled={snapshotBusy} data-product-tour="gesture-play">
              <Play aria-hidden="true" size={16} />
              再生
            </button>
            <button
              type="button"
              className="gesture-creator-tool-button"
              onClick={stopPreview}
              disabled={!previewPlaying || snapshotBusy}
            >
              <Square aria-hidden="true" size={15} />
              停止
            </button>
            <button
              type="button"
              className="gesture-creator-delete-button"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={!motionId || snapshotBusy}
              data-product-tour="gesture-delete"
            >
              <Trash2 aria-hidden="true" size={16} />
              削除
            </button>
            {snapshotBusy && <p className="gesture-creator-loading" role="status">よみこみ中…</p>}
            {error && <p className="gesture-creator-error" role="alert">{error}</p>}
          </div>
        </div>
        <button
          type="button"
          className="gesture-creator-overlay__close"
          onClick={onClose}
          aria-label="シミュレーター画面を閉じる"
        >
          <X aria-hidden="true" size={20} />
        </button>
      </header>

      <section className="gesture-creator-timeline" aria-label="ジェスチャーのタイムライン" data-product-tour="gesture-timeline">
        <div className="gesture-creator-timeline__heading">
          <h3>タイムライン</h3>
          <span>{frames.length}フレーム</span>
        </div>
        <input
          type="range"
          className="gesture-creator-timeline__slider"
          min={0}
          max={Math.max(0, frames.length - 1)}
          step={1}
          value={selectedFrameIndex}
          disabled={snapshotBusy || frames.length < 2}
          onChange={(event) => selectFrame(Number(event.target.value))}
          aria-label="タイムラインの現在位置"
        />
        <div className="gesture-creator-timeline__track">
          <div className="gesture-creator-timeline__steps">
            {frames.map((frame, index) => {
              const locked = index === 0;
              const hasPose = Object.keys(frame.joints).length > 0;
              const snapshot = hasPose ? snapshots[frame.key] : undefined;
              const frameLabel = locked ? '基本姿勢（固定）' : `${index + 1}番目のフレーム`;
              const classNames = [
                'gesture-creator-timeline__step-group',
                locked ? 'is-locked' : '',
                !hasPose ? 'is-empty' : '',
                dragFrameIndex === index ? 'is-dragging' : '',
                dropFrameIndex === index ? 'is-drop-target' : '',
              ].filter(Boolean).join(' ');
              return (
                <div
                  key={frame.key}
                  className={classNames}
                  data-frame-index={index}
                  onPointerDown={handleFrameDragPointerDown(index, locked)}
                  onPointerMove={handleFrameDragPointerMove}
                  onPointerUp={handleFrameDragPointerUp}
                  onPointerCancel={handleFrameDragPointerCancel}
                  data-product-tour={tourMode === 'greeting' && index === 3 ? 'greeting-hold-frame' : undefined}
                >
                  <div
                    role="button"
                    tabIndex={snapshotBusy ? -1 : 0}
                    className={`gesture-creator-timeline__step${selectedFrameIndex === index ? ' is-selected' : ''}${hasPose ? ' has-pose' : ''}`}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      selectFrame(index);
                    }}
                    aria-pressed={selectedFrameIndex === index}
                    aria-label={frameLabel}
                    aria-disabled={snapshotBusy}
                    title={frameLabel}
                  >
                    {snapshot && (
                      <img className="gesture-creator-timeline__step-thumb" src={snapshot} alt="" />
                    )}
                  </div>
                  <div className="gesture-creator-timeline__step-actions">
                    {locked ? (
                      <span className="gesture-creator-timeline__step-lock" title="基本姿勢は変更できません">
                        <Lock aria-hidden="true" size={14} />
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={`gesture-creator-timeline__step-action${hasPose ? ' is-delete' : ' is-update'}`}
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={() => (hasPose ? clearFrame(index) : updateFrame(index))}
                        disabled={snapshotBusy}
                        data-product-tour={index === 1 ? 'gesture-frame' : undefined}
                        aria-label={hasPose ? `${frameLabel}の姿勢を削除` : `${frameLabel}に現在の姿勢を登録`}
                      >
                        {hasPose ? <Trash2 aria-hidden="true" size={14} /> : <Plus aria-hidden="true" size={14} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="gesture-creator-timeline__track-actions">
            <button
              type="button"
              className="gesture-creator-timeline__add"
              onClick={insertFrame}
              disabled={snapshotBusy}
              aria-label="選択中のフレームの次に空フレームを追加"
              title="選択中のフレームの次に空フレームを追加"
            >
              <Plus aria-hidden="true" size={18} />
            </button>
            {shouldShowEmptyFrameDelete(previewPlaying, selectedFrameIndex, frames[selectedFrameIndex]) && (
              <button
                type="button"
                className="gesture-creator-timeline__delete-frame"
                onClick={() => removeFrame(selectedFrameIndex)}
                disabled={snapshotBusy}
                aria-label={`${selectedFrameIndex + 1}番目の空フレームを削除`}
                title="選択中の空フレームを削除"
              >
                <Trash2 aria-hidden="true" size={18} />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className={`gesture-creator-overlay__body${snapshotBusy ? ' is-busy' : ''}`} aria-busy={snapshotBusy} data-product-tour="gesture-robot">
        <VirtualRobot
          jointDragEnabled
          physicsEnabled={previewPlaying}
          showCollisionBoxes={showCollisionBoxes}
          eyeLedColor={ledColor ?? undefined}
          onJointSelectionChange={setSelectedJoint}
          onJointControlsChange={setJointControls}
          onJointControlReady={handleJointControlReady}
          onJointPoseReady={handleJointPoseReady}
          onBodyRotationControlReady={handleBodyRotationReady}
          onBodyRotationChange={handleBodyRotationChange}
          onSnapshotReady={handleSnapshotReady}
          onMotionElapsedChange={handleMotionElapsedChange}
        />
        <label className="gesture-creator-collision-toggle hidden">
          <input
            type="checkbox"
            checked={showCollisionBoxes}
            onChange={(event) => setShowCollisionBoxes(event.target.checked)}
          />
          <span>衝突Boxを表示</span>
        </label>
        <aside className="gesture-creator-inspector" aria-label="関節の詳細調整">
          <section className="gesture-creator-control-section gesture-creator-control-card" data-product-tour="gesture-joints">
            <div className={`gesture-creator-control-section__heading${jointControlsOpen ? ' is-open' : ''}`}>
              <button
                type="button"
                className="gesture-creator-control-section__toggle"
                onClick={() => setJointControlsOpen((open) => !open)}
                aria-expanded={jointControlsOpen}
              >
                {jointControlsOpen ? <ChevronDown aria-hidden="true" size={16} /> : <ChevronRight aria-hidden="true" size={16} />}
                関節の調整
              </button>
              <button
                type="button"
                className={`gesture-creator-joint-read-button${robotAnglesLoading ? ' is-loading' : ''}`}
                onClick={() => void readRobotAngles()}
                disabled={robotAngleReadButtonState.disabled}
                aria-label={robotAngleReadButtonState.ariaLabel}
                title={robotAngleReadButtonState.title}
              >
                {robotAnglesLoading ? (
                  <>
                    <LoaderCircle aria-hidden="true" className="animate-spin" size={16} />
                    <span>Loading...</span>
                  </>
                ) : <CloudDownload aria-hidden="true" size={16} />}
              </button>
            </div>
          {jointControlsOpen && (jointControls.length > 0 ? (
            <>
            {selectedJoint?.id === 'BODY_link' && (
              <div className="gesture-creator-body-rotation" aria-label="Bodyの回転">
                <p>Body</p>
                {BODY_ROTATION_AXES.map(({ axis, label: axisLabel }) => (
                  <div className="gesture-creator-joint-adjustment" key={axis}>
                    <label className="gesture-creator-inspector__control">
                      <span>{axisLabel}</span>
                      <input
                        type="range"
                        min={-180}
                        max={180}
                        step={0.1}
                        value={bodyRotation[axis]}
                        disabled={previewPlaying}
                        onChange={(event) => updateBodyRotation(axis, event.currentTarget.valueAsNumber)}
                      />
                    </label>
                    <label className="gesture-creator-joint-list__value">
                      <input
                        type="number"
                        min={-180}
                        max={180}
                        step={0.1}
                        value={bodyRotation[axis]}
                        disabled={previewPlaying}
                        aria-label={`Bodyの${axisLabel}回転`}
                        onChange={(event) => updateBodyRotation(axis, event.currentTarget.valueAsNumber)}
                      />
                      <span>度</span>
                    </label>
                  </div>
                ))}
              </div>
            )}
            <ul className="gesture-creator-joint-list">
              {jointControls.map((joint) => {
                const selected = selectedJoint?.id === joint.id;
                const adjusting = adjustingJointId === joint.id;
                return (
                  <li
                    key={joint.id}
                    ref={(element) => { jointCardRefs.current[joint.id] = element; }}
                    className={`${selected ? ' is-selected' : ''}${adjusting ? ' is-adjusting' : ''}`}
                  >
                    <p title={joint.label}>{joint.label}</p>
                    <div className="gesture-creator-joint-adjustment">
                      <label className="gesture-creator-inspector__control">
                        <span className="sr-only">{joint.label}の角度</span>
                        <input
                          type="range"
                          min={joint.min}
                          max={joint.max}
                          step={1}
                          value={joint.value}
                          onChange={(event) => {
                            if (Number.isFinite(event.currentTarget.valueAsNumber)) {
                              updateJointValue(joint.id, Math.round(event.currentTarget.valueAsNumber));
                            }
                          }}
                          onFocus={() => setAdjustingJointId(joint.id)}
                          onBlur={() => setAdjustingJointId(null)}
                        />
                      </label>
                      <label className="gesture-creator-joint-list__value">
                        <JointNumberInput
                          joint={joint}
                          onValueChange={(value) => updateJointValue(joint.id, value)}
                          onFocus={() => setAdjustingJointId(joint.id)}
                          onBlur={() => setAdjustingJointId(null)}
                        />
                        <span>{joint.unit === 'deg' ? '度' : 'm'}</span>
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>
            </>
          ) : (
            <p className="gesture-creator-inspector__empty">関節を読み込み中です</p>
          ))}
          </section>

          <section className="gesture-creator-control-section gesture-creator-control-card">
            <button
              type="button"
              className="gesture-creator-control-section__toggle"
              onClick={() => setSoundControlsOpen((open) => !open)}
              aria-expanded={soundControlsOpen}
            >
              {soundControlsOpen ? <ChevronDown aria-hidden="true" size={16} /> : <ChevronRight aria-hidden="true" size={16} />}
              効果音
            </button>
            {soundControlsOpen && (
              <label className="gesture-creator-sound-control">
                <span>効果音をえらぶ</span>
                <select
                  value={sound ?? ''}
                  onChange={(event) => updateSound(event.target.value ? event.target.value as SoundEffect : null)}
                >
                  <option value="">なし</option>
                  {SOUND_EFFECTS.map((effect) => <option key={effect} value={effect}>{effect}</option>)}
                </select>
              </label>
            )}
          </section>
        </aside>
      </div>

      {saveDialogOpen && (
        <div className="gesture-creator-load-dialog" role="presentation" onClick={() => setSaveDialogOpen(false)}>
          <section
            className="gesture-creator-load-dialog__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gesture-creator-save-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="gesture-creator-load-dialog__header">
              <h2 id="gesture-creator-save-title">ジェスチャーを保存</h2>
              <button type="button" onClick={() => setSaveDialogOpen(false)} aria-label="保存を閉じる">
                <X aria-hidden="true" size={18} />
              </button>
            </header>
            <label className="gesture-creator-save-dialog__field">
              <span>ジェスチャーの名前</span>
              <input
                type="text"
                autoFocus
                value={saveDialogLabel}
                onChange={(event) => setSaveDialogLabel(event.target.value)}
                placeholder="れい: おおきくてをふる"
              />
            </label>
            <footer className="gesture-creator-load-dialog__actions">
              <button type="button" className="gesture-creator-tool-button" onClick={() => setSaveDialogOpen(false)}>
                キャンセル
              </button>
              <button type="button" className="gesture-creator-submit" onClick={confirmSaveMotion} data-product-tour="gesture-save-confirm">
                <Save aria-hidden="true" size={16} />
                保存
              </button>
            </footer>
          </section>
        </div>
      )}

      {deleteDialogOpen && (
        <div className="gesture-creator-load-dialog" role="presentation" onClick={() => setDeleteDialogOpen(false)}>
          <section
            className="gesture-creator-load-dialog__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gesture-creator-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="gesture-creator-load-dialog__header">
              <h2 id="gesture-creator-delete-title">ジェスチャーを削除</h2>
              <button type="button" onClick={() => setDeleteDialogOpen(false)} aria-label="削除を閉じる">
                <X aria-hidden="true" size={18} />
              </button>
            </header>
            <p className="gesture-creator-delete-dialog__message">消えますよ？</p>
            <footer className="gesture-creator-load-dialog__actions">
              <button type="button" className="gesture-creator-tool-button" onClick={() => setDeleteDialogOpen(false)}>
                キャンセル
              </button>
              <button type="button" className="gesture-creator-delete-button" onClick={deleteMotion} data-product-tour="gesture-delete-confirm">
                <Trash2 aria-hidden="true" size={16} />
                削除
              </button>
            </footer>
          </section>
        </div>
      )}

      {loadDialogOpen && (
        <div className="gesture-creator-load-dialog" role="presentation" onClick={() => setLoadDialogOpen(false)}>
          <section
            className="gesture-creator-load-dialog__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gesture-creator-load-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="gesture-creator-load-dialog__header">
              <h2 id="gesture-creator-load-title">自作ジェスチャーを読み込む</h2>
              <button type="button" onClick={() => setLoadDialogOpen(false)} aria-label="読み込みを閉じる">
                <X aria-hidden="true" size={18} />
              </button>
            </header>
            {customMotions.length > 0 ? (
              <div className="gesture-creator-load-dialog__list">
                {customMotions.map((motion) => (
                  <label key={motion.id} className={`gesture-creator-load-dialog__item${loadDialogMotionId === motion.id ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="custom-gesture-load"
                      value={motion.id}
                      checked={loadDialogMotionId === motion.id}
                      onChange={(event) => setLoadDialogMotionId(event.target.value)}
                    />
                    <span>
                      <strong>{motion.label}</strong>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="gesture-creator-load-dialog__empty">保存された自作ジェスチャーはありません</p>
            )}
            <section className="gesture-creator-load-dialog__section" aria-labelledby="gesture-library-title">
              <h3 id="gesture-library-title">Gesture</h3>
              {gestureLibraryStatus === 'loading' && (
                <p className="gesture-creator-load-dialog__empty">Gestureを読み込み中です</p>
              )}
              {gestureLibraryStatus === 'error' && (
                <p className="gesture-creator-load-dialog__empty">Gestureを読み込めませんでした</p>
              )}
              {gestureLibraryStatus === 'ready' && (
                <div className="gesture-creator-load-dialog__list">
                  {GESTURE_MOTIONS.map((gesture) => {
                    const available = gestureLibrary.some((motion) => String(motion.motionNo) === gesture.id);
                    return (
                      <label key={gesture.id} className="gesture-creator-load-dialog__item">
                        <input
                          type="radio"
                          name="built-in-gesture-load"
                          value={gesture.id}
                          disabled={!available || snapshotBusy}
                          onChange={(event) => importGestureMotion(event.target.value)}
                        />
                        <span>
                          <strong>{gesture.label}</strong>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </section>
            <footer className="gesture-creator-load-dialog__actions">
              <button type="button" className="gesture-creator-tool-button" onClick={() => setLoadDialogOpen(false)}>
                キャンセル
              </button>
              <button type="button" className="gesture-creator-submit" onClick={confirmLoadMotion} disabled={!loadDialogMotionId}>
                読み込む
              </button>
            </footer>
          </section>
        </div>
      )}

      {tourPromptOpen && (
        <div className="gesture-creator-tour-prompt" role="presentation">
          <section
            className="gesture-creator-tour-prompt__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gesture-creator-tour-prompt-title"
            aria-describedby="gesture-creator-tour-prompt-description"
          >
            <h2 id="gesture-creator-tour-prompt-title">Createの使い方</h2>
            <p id="gesture-creator-tour-prompt-description">
              シミュレーターでジェスチャーを作る手順を確認しますか？
            </p>
            <footer className="gesture-creator-tour-prompt__actions">
              <button type="button" onClick={() => resolveTourPrompt(false)}>
                スキップ
              </button>
              <button type="button" autoFocus onClick={() => resolveTourPrompt(true)}>
                説明を確認
              </button>
            </footer>
          </section>
        </div>
      )}

      {tourMode === 'guide' && (
        <ProductTour
          key="gesture-guide"
          steps={GESTURE_TOUR_STEPS}
          completionLabel="挨拶モーションを作ってみる"
          onComplete={startGreetingTour}
          onClose={() => setTourMode(null)}
        />
      )}
      {tourMode === 'greeting' && (
        <ProductTour
          key="greeting-practice"
          steps={GREETING_TOUR_STEPS}
          onClose={() => setTourMode(null)}
        />
      )}

      {/* <footer className="gesture-creator-overlay__footer" aria-label="つくったジェスチャー">
        {customMotions.length === 0 ? (
          <p className="gesture-creator-empty">まだありません</p>
        ) : (
          <ul className="gesture-creator-list">
            {customMotions.map((motion) => (
              <li key={motion.id}>
                <span className="gesture-creator-list__label">{motion.label}</span>
                <span className="gesture-creator-list__id">ID: {motion.id}</span>
                <button
                  type="button"
                  onClick={() => removeCustomMotion(motion.id)}
                  aria-label={`${motion.label}を削除`}
                >
                  <Trash2 aria-hidden="true" size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </footer> */}
    </div>,
    document.body,
  );
}
